import type { ConversationPanelState } from "@circle-react-uikit/CircleAIAppShell/types";
import { useChatStore } from "@circle-react/hooks/chatsV2/store/useChatStore";
import { aiAgentQueries } from "@circle-react/lib/queries/aiAgents";
import { useToast } from "@circleco/compass/context/CompassContext";
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "react-query";

import { t } from "@/i18n-js/instance";

import { useAvatarConversationPanelStore } from "../useAvatarConversationPanelStore";

export const useOpenSharedAvatar = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();

  return async (
    avatarId: number,
    panelState: Exclude<ConversationPanelState, "closed">,
  ) => {
    const openPanelOnAvatar = () =>
      useAvatarConversationPanelStore
        .getState()
        .openPanel({ chatId: null, avatarId, panelState });

    const { records: widgetAvatars } = await queryClient.fetchQuery(
      aiAgentQueries.memberAvatars(),
    );

    // Enabled and chattable in the widget: open the conversation there.
    if (widgetAvatars.some(({ id }) => id === avatarId)) {
      openPanelOnAvatar();
      return;
    }

    const { records: directMessageAvatars } = await queryClient.fetchQuery(
      aiAgentQueries.directMessageAvatars(),
    );
    const directMessageAvatar = directMessageAvatars.find(
      ({ id }) => id === avatarId,
    );

    // Enabled but not chattable in the widget: start a new chat in the DM modal.
    if (directMessageAvatar) {
      useChatStore.getState().inbox.setCurrentBotProfile(directMessageAvatar);
      void navigate({ to: "/messages/$uuid", params: { uuid: "new" } });
      return;
    }

    // Not reachable by either channel (e.g. disabled): tell the member why
    // nothing opened for the avatar in the link.
    toast.add({
      title: t("avatars.share_link.unavailable"),
      variant: "primary",
      icon: "circle-info",
      iconColor: "info",
    });

    // The panel can still open in its general state when other avatars are
    // available; with none, the toast is all there is.
    if (widgetAvatars.length > 0) openPanelOnAvatar();
  };
};
